import { getWithoutFalsyOrUnwantedKeys } from '../../libs/objects';
import { ApartmentClientInformation, ApartmentConfirmation, ApartmentInformation, MainOwner } from '../../types/apartments';
import { ApartmentClientInformationFormData, ApartmentConfirmationFormData } from '../../types/forms/createApartment';
import { showWaterFee } from '../../utils/apartments';
const deleteUnneededClientInfo = (clientData: ApartmentClientInformationFormData): ApartmentClientInformation => {
    const unwantedClientKeys = [
        'hasOtherOwners' as const,
        'clientType' as const,
        'apartment' as const,
        // Delete otherOwners if checkbox is "no other owners"
        ...(clientData?.hasOtherOwners === 'false' ? ['otherOwners' as const] : [])
    ];
    const unwantedMainOwnerKeys = [
        'email' as const,
        'language' as const,
        'phoneNumber' as const,
        'name' as const,
        'autocompleteLocation' as const,
        // Delete business props if client is private
        ...(clientData?.clientType === 'private'
            ? ['businessId' as const, 'companyName' as const]
            : [])
    ];
    const cleanedClientData = getWithoutFalsyOrUnwantedKeys<ApartmentClientInformation, typeof unwantedClientKeys>({
        object: clientData,
        unwantedKeys: unwantedClientKeys,
        keepZeroes: true
    });
    const cleanedMainOwner = getWithoutFalsyOrUnwantedKeys<MainOwner, typeof unwantedMainOwnerKeys>({
        object: cleanedClientData.mainOwner,
        unwantedKeys: unwantedMainOwnerKeys,
        keepZeroes: true
    });
    const newClientData: ApartmentClientInformation = {
        ...cleanedClientData,
        mainOwner: cleanedMainOwner
    };
    return newClientData;
};
const deleteUnneededApartmentInfo = (info: ApartmentInformation) => {
    const noCarHeatingSocketNeeded = info?.carHeatingSocket &&
        (info.parking === 'no' || info.parking === 'notSure');
    const noHeatingDescriptionNeeded = info?.heatingDescription && info.heating !== 'other';
    const unwantedInfoKeys = [
        'autocompleteLocation' as const,
        ...(noCarHeatingSocketNeeded ? ['carHeatingSocket' as const] : []),
        ...(noHeatingDescriptionNeeded ? ['heatingDescription' as const] : [])
    ];
    const cleanedInfoData = getWithoutFalsyOrUnwantedKeys<ApartmentInformation, typeof unwantedInfoKeys>({
        object: info,
        unwantedKeys: unwantedInfoKeys,
        keepZeroes: true
    });
    return cleanedInfoData;
};
const deleteUnneededConfirmData = (confirmation: ApartmentConfirmationFormData, parkingFeeNeeded: boolean, useCase?: 'beforeLogin') => {
    const unwantedConfirmKeys = [
        'isSVAMember' as const,
        'autocompleteLocation' as const,
        ...(confirmation.waterFee && !showWaterFee(confirmation.waterFeeBasis)
            ? ['waterFee' as const]
            : []),
        ...(confirmation.availableUntilDate &&
            !(confirmation.rentPeriod === 'temporary')
            ? ['availableUntilDate' as const]
            : []),
        ...(confirmation.securityDepositAmount &&
            (confirmation.securityDeposit as any) !== 'other'
            ? ['securityDepositAmount' as const]
            : []),
        ...(confirmation.parkingFee && !parkingFeeNeeded
            ? ['parkingFee' as const]
            : []),
        ...((!confirmation.isSVAMember || confirmation.isSVAMember === 'false') &&
            confirmation.promocode
            ? ['promocode' as const]
            : []),
        ...(useCase
            ? [
                'email' as const,
                'phoneNumber' as const,
                'password' as const,
                'confirmPassword' as const,
                'name' as const
            ]
            : [])
    ];
    const cleanedConfirmation = getWithoutFalsyOrUnwantedKeys<ApartmentConfirmation, typeof unwantedConfirmKeys>({
        object: confirmation,
        unwantedKeys: unwantedConfirmKeys,
        keepZeroes: true,
        keepFalseBooleans: true
    });
    return cleanedConfirmation;
};
export { deleteUnneededApartmentInfo, deleteUnneededClientInfo, deleteUnneededConfirmData };
